import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { Webhook, ShieldCheck, ShieldX, ShieldQuestion, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const MASKED_KEYS = ['firstname', 'lastname', 'email', 'phone'];
const STATE_KEYS = ['drivercomments', 'meetandgreetsigntext'];

const maskPII = (value) => {
  if (Array.isArray(value)) return value.map(maskPII);
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const lk = k.toLowerCase();
      if (MASKED_KEYS.includes(lk)) {
        out[k] = '***';
      } else if (STATE_KEYS.includes(lk)) {
        out[k] = (v === null || v === undefined || String(v).trim() === '') ? '(vuoto)' : '(compilato)';
      } else {
        out[k] = maskPII(v);
      }
    }
    return out;
  }
  return value;
};

const JOURNEY_ID_KEYS = ['journeyid'];
const JOURNEY_CODE_KEYS = ['journeyreference', 'journeycode', 'bookingreference', 'bookingnumber', 'reference', 'code'];

const findByKeys = (obj, keys, depth = 0) => {
  if (!obj || typeof obj !== 'object' || depth > 4) return null;
  for (const [k, v] of Object.entries(obj)) {
    if (keys.includes(k.toLowerCase()) && (typeof v === 'string' || typeof v === 'number')) {
      return String(v);
    }
  }
  for (const v of Object.values(obj)) {
    if (v && typeof v === 'object') {
      const found = findByKeys(v, keys, depth + 1);
      if (found) return found;
    }
  }
  return null;
};

const eventType = (payload) => {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const topKeys = Object.keys(payload);
    // Transferz format: the event type is the main top-level JSON key,
    // e.g. { "journeyFleetAssignedEvent": {...} }
    const eventKey = topKeys.find(k => k.toLowerCase().endsWith('event'));
    if (eventKey) return eventKey;
    if (topKeys.length === 1 && payload[topKeys[0]] && typeof payload[topKeys[0]] === 'object') {
      return topKeys[0];
    }
    return payload.event || payload.eventType || payload.type || payload.topic || '—';
  }
  if (typeof payload === 'string') return payload.slice(0, 40) || '—';
  return '—';
};

const SignatureBadge = ({ ev }) => {
  if (!ev.signature_present) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600" data-testid="sig-badge-absent">
        <ShieldQuestion className="h-3.5 w-3.5" /> Assente
      </span>
    );
  }
  if (ev.signature_valid) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700" data-testid="sig-badge-valid">
        <ShieldCheck className="h-3.5 w-3.5" /> Valida
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700" data-testid="sig-badge-invalid">
      <ShieldX className="h-3.5 w-3.5" /> Non valida
    </span>
  );
};

export default function WebhookEvents() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/webhooks/transferz/events?limit=100`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => setEvents(r.data))
      .catch(() => setError('Impossibile caricare gli eventi webhook'))
      .finally(() => setLoading(false));
  }, [token]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return iso;
    }
  };

  return (
    <div className="p-4 lg:p-8" data-testid="webhook-events-panel">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
          <Webhook className="h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Webhook Events</h1>
          <p className="text-sm text-slate-500">Eventi ricevuti da Transferz — accesso riservato agli admin</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento...
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4" data-testid="webhook-events-error">{error}</div>
      ) : events.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500" data-testid="webhook-events-empty">
          Nessun evento webhook ricevuto finora.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full" data-testid="webhook-events-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tipo evento</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Data e ora</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Firma</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Metodo</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Journey ID</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Codice viaggio</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedEvent(ev)} data-testid={`webhook-event-row-${ev.id}`}>
                  <td className="px-4 py-3 text-sm font-medium text-slate-800">{eventType(ev.payload)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{formatDate(ev.received_at)}</td>
                  <td className="px-4 py-3"><SignatureBadge ev={ev} /></td>
                  <td className="px-4 py-3 text-sm text-slate-600">{ev.signature_method || '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-800 font-mono" data-testid={`journey-id-${ev.id}`}>{findByKeys(ev.payload, JOURNEY_ID_KEYS) || '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-800 font-mono" data-testid={`journey-code-${ev.id}`}>{findByKeys(ev.payload, JOURNEY_CODE_KEYS) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Event detail dialog — payload with PII masked, never shows Authorization header or HMAC key */}
      <Dialog open={!!selectedEvent} onOpenChange={(open) => !open && setSelectedEvent(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="webhook-event-detail-dialog">
          {selectedEvent && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Webhook className="h-5 w-5 text-indigo-600" />
                  {eventType(selectedEvent.payload)}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-slate-500">{formatDate(selectedEvent.received_at)}</span>
                  <SignatureBadge ev={selectedEvent} />
                  {selectedEvent.signature_method && (
                    <span className="text-xs text-slate-500">metodo: {selectedEvent.signature_method}</span>
                  )}
                </div>
                <div className="text-xs text-slate-400">I dati personali dei viaggiatori sono mascherati con ***</div>
                <pre className="bg-slate-900 text-slate-100 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-words" data-testid="webhook-event-payload">
{typeof selectedEvent.payload === 'object' && selectedEvent.payload !== null
  ? JSON.stringify(maskPII(selectedEvent.payload), null, 2)
  : String(selectedEvent.payload ?? '(vuoto)')}
                </pre>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
